/**
 * @fileoverview Gestão da blocklist/opt-out global da empresa: adicionar números
 * (um ou vários), buscar e remover. Números que nunca devem receber disparo.
 * Renderizado como aba dentro do modal "Gerenciar Bases".
 */

import React, { useState, useEffect, useCallback } from "react";
import { CircularProgress } from "@material-ui/core";
import { Plus, Trash2, Search, ShieldOff } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../../../services/api";
import toastError from "../../../../errors/toastError";

const C = {
  border: "rgba(255,255,255,0.08)",
  faint: "rgba(255,255,255,0.5)",
  text: "rgba(255,255,255,0.85)",
};

const input = {
  padding: "9px 12px", borderRadius: 8, background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.9)",
  fontSize: 13, outline: "none", boxSizing: "border-box",
};

export default function OptOutManager() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [phone, setPhone] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async (s = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/mass-send/optouts", { params: { search: s } });
      setRows(data.optOuts || []);
      setCount(data.count || 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const add = async () => {
    const list = phone.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) return;
    setAdding(true);
    try {
      const { data } = await api.post("/mass-send/optouts", { phones: list });
      toast.success(
        `${data.added} adicionado(s)` +
          (data.invalid && data.invalid.length ? `, ${data.invalid.length} inválido(s)` : "")
      );
      setPhone("");
      load(search);
    } catch (err) {
      toastError(err);
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este número da lista de bloqueio?")) return;
    try {
      await api.delete(`/mass-send/optouts/${id}`);
      load(search);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 16 }}>
        <textarea
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Telefones para bloquear (um por linha, ou separados por vírgula)"
          rows={2}
          style={{ ...input, flex: 1, resize: "vertical", fontFamily: "inherit" }}
        />
        <button
          onClick={add}
          disabled={adding || !phone.trim()}
          onMouseEnter={(e) => { if (!(adding || !phone.trim())) e.currentTarget.style.background = "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)"; }}
          style={{
            height: 36, padding: "0 14px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
            color: "#ffffff", fontSize: "0.875rem", fontWeight: 500, textTransform: "none",
            cursor: (adding || !phone.trim()) ? "default" : "pointer",
            opacity: (adding || !phone.trim()) ? 0.6 : 1,
            display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap",
            transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)", flexShrink: 0,
          }}
        >
          <Plus size={15} strokeWidth={1.75} /> {adding ? "Adicionando…" : "Bloquear"}
        </button>
      </div>

      <div style={{ position: "relative", marginBottom: 12 }}>
        <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: C.faint }} />
        <input
          placeholder="Buscar número…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...input, width: "100%", paddingLeft: 32 }}
        />
      </div>

      {loading ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <CircularProgress size={20} style={{ color: "#818cf8" }} />
        </div>
      ) : rows.length === 0 ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, color: C.faint, fontSize: 13 }}>
          <ShieldOff size={22} style={{ marginBottom: 8, opacity: 0.6 }} />
          <div>Nenhum número bloqueado.</div>
        </div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "8px 12px", fontSize: 11, color: C.faint, background: "rgba(255,255,255,0.02)" }}>
            {count} número(s) bloqueado(s)
          </div>
          {rows.map((r) => (
            <div
              key={r.id}
              style={{
                display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                borderTop: `1px solid ${C.border}`,
              }}
            >
              <span style={{ flex: 1, fontSize: 13, color: C.text }}>{r.phone}</span>
              <span style={{ fontSize: 11, color: C.faint }}>{r.reason}</span>
              <span onClick={() => remove(r.id)} title="Remover" style={{ cursor: "pointer", color: "#f87171", padding: 4 }}>
                <Trash2 size={14} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
